import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { AxiosInstance } from 'axios';

interface EmailTemplate {
  id: number;
  name: string;
  subject: string;
  content: string;
  description: string | null;
  variables: string[] | null;
  isActive: boolean;
}

interface CreateTemplateForm {
  name: string;
  subject: string;
  content: string;
  description: string;
  variables: string[];
}

interface Notification {
  type: 'success' | 'error' | 'info';
  message: string;
  id: number;
}

export const EmailTemplateEditor: React.FC<{
  api: AxiosInstance;
  onTemplateCreated?: () => void;
}> = ({ api, onTemplateCreated }) => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [form, setForm] = useState<CreateTemplateForm>({
    name: '',
    subject: '',
    content: '',
    description: '',
    variables: [],
  });
  const [variableInput, setVariableInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Ajouter une notification
  const addNotification = (type: 'success' | 'error' | 'info', message: string) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { type, message, id }]);
    
    // Auto-remove après 5 secondes
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  };

  // Charger les templates
  const fetchTemplates = async () => {
    try {
      console.log('[TEMPLATE] Chargement des templates...');
      const response = await api.get('/email-templates');
      console.log('[TEMPLATE] Réponse API:', response.data);
      
      // Gérer la structure de réponse
      let templatesData: EmailTemplate[] = [];
      if (Array.isArray(response.data)) {
        templatesData = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        templatesData = response.data.data;
      } else if (response.data?.templates && Array.isArray(response.data.templates)) {
        templatesData = response.data.templates;
      }
      
      setTemplates(templatesData);
      console.log('[TEMPLATE] Templates chargés:', templatesData.length);
    } catch (error: any) {
      console.error('[TEMPLATE] Erreur chargement:', error);
      if (error.response?.status === 401) {
        addNotification('error', 'Authentification requise');
      } else {
        addNotification('error', 'Impossible de charger les templates');
      }
    }
  };

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Créer un nouveau template
  const handleCreateTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!form.name.trim()) {
      addNotification('error', 'Le nom du template est requis');
      return;
    }
    if (!form.subject.trim()) {
      addNotification('error', 'Le sujet est requis');
      return;
    }
    if (!form.content.trim()) {
      addNotification('error', 'Le contenu est requis');
      return;
    }

    setLoading(true);

    try {
      console.log('[TEMPLATE] Création template avec données:', {
        name: form.name,
        subject: form.subject,
        description: form.description,
        variables: form.variables,
      });

      const payload = {
        name: form.name,
        subject: form.subject,
        content: form.content,
        description: form.description || '',
        variables: form.variables.length > 0 ? form.variables : undefined,
      };

      console.log('[TEMPLATE] Payload envoyé:', payload);

      const response = await api.post('/email-templates', payload);

      console.log('[TEMPLATE] Réponse création:', response.data);

      // Réinitialiser le formulaire
      setForm({
        name: '',
        subject: '',
        content: '',
        description: '',
        variables: [],
      });
      setVariableInput('');
      setShowForm(false);

      // Recharger les templates
      await fetchTemplates();
      onTemplateCreated?.();

      addNotification('success', '✅ Template créé avec succès!');
    } catch (error: any) {
      console.error('[TEMPLATE] Erreur création:', error);
      console.error('[TEMPLATE] Response data:', error.response?.data);
      
      let errorMsg = 'Erreur lors de la création du template';
      if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.response?.status === 401) {
        errorMsg = 'Authentification requise';
      } else if (error.response?.status === 403) {
        errorMsg = 'Accès non autorisé';
      }
      
      addNotification('error', `❌ ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  // Ajouter une variable
  const handleAddVariable = () => {
    if (variableInput.trim() && !form.variables.includes(variableInput.trim())) {
      setForm({
        ...form,
        variables: [...form.variables, variableInput.trim()],
      });
      setVariableInput('');
    }
  };

  // Supprimer une variable
  const handleRemoveVariable = (variable: string) => {
    setForm({
      ...form,
      variables: form.variables.filter((v) => v !== variable),
    });
  };

  // Modules Quill
  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ script: 'sub' }, { script: 'super' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ size: ['small', false, 'large', 'huge'] }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      ['clean'],
      ['link', 'image'],
    ],
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-lg">
      {/* Notifications Toast */}
      <div className="fixed top-4 right-4 z-50 space-y-2 max-w-md">
        {notifications.map((notif) => (
          <div
            key={notif.id}
            className={`p-4 rounded-lg shadow-lg animate-slide-in ${
              notif.type === 'success'
                ? 'bg-green-50 border-l-4 border-green-500 text-green-800'
                : notif.type === 'error'
                ? 'bg-red-50 border-l-4 border-red-500 text-red-800'
                : 'bg-blue-50 border-l-4 border-blue-500 text-blue-800'
            }`}
          >
            <p className="font-semibold">{notif.message}</p>
          </div>
        ))}
      </div>

      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">📧 Éditeur de Templates</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition cursor-pointer"
        >
          {showForm ? 'Annuler' : 'Créer un template'}
        </button>
      </div>

      {showForm ? (
        <form onSubmit={handleCreateTemplate} className="space-y-6">
          {/* Nom du template */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Nom du template *
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Ex: Annonce Tournoi"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
              required
            />
          </div>

          {/* Sujet */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Sujet (objet du mail) *
            </label>
            <input
              type="text"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              placeholder="Ex: 🎾 {{tournamentName}} - {{tournamentDate}}"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Utilisez {`{{variable}}`} pour insérer les variables
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Description
            </label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Ex: Template pour annoncer un nouveau tournoi"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
            />
          </div>

          {/* Variables */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Variables dynamiques
            </label>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={variableInput}
                onChange={(e) => setVariableInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddVariable();
                  }
                }}
                placeholder="Ex: tournamentName"
                className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
              />
              <button
                type="button"
                onClick={handleAddVariable}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Ajouter
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {form.variables.map((variable) => (
                <span
                  key={variable}
                  className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm flex items-center gap-2"
                >
                  {`{{${variable}}}`}
                  <button
                    type="button"
                    onClick={() => handleRemoveVariable(variable)}
                    className="text-red-500 hover:text-red-700 font-bold"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Contenu HTML */}
          <div className="relative z-10">
            <label className="block text-sm font-medium mb-2">
              Contenu du mail (HTML) *
            </label>
            <div style={{ height: '350px', position: 'relative', zIndex: 1 }}>
              <ReactQuill
                value={form.content}
                onChange={(value) => setForm({ ...form, content: value })}
                modules={modules}
                theme="snow"
                placeholder="Rédigez le contenu de votre email..."
                style={{ height: '100%', zIndex: 1 }}
              />
            </div>
          </div>

          {/* Boutons d'action - Z-index élevé */}
          <div className="flex gap-3 justify-end relative z-50">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100 cursor-pointer"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-400 cursor-pointer"
            >
              {loading ? 'Création...' : 'Créer le template'}
            </button>
          </div>
        </form>
      ) : (
        <div>
          <h3 className="text-lg font-semibold mb-4">Templates disponibles</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {templates.map((template) => (
              <div
                key={template.id}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-lg cursor-pointer transition"
                onClick={() => setSelectedTemplate(template)}
              >
                <h4 className="font-bold text-blue-600 mb-2">{template.name}</h4>
                <p className="text-sm text-gray-600 mb-2">{template.subject}</p>
                <p className="text-xs text-gray-500">{template.description}</p>
                {template.variables && template.variables.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs font-semibold text-gray-700">Variables:</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {template.variables.map((v) => (
                        <span
                          key={v}
                          className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                        >
                          {`{{${v}}}`}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Aperçu du template sélectionné */}
          {selectedTemplate && (
            <div className="mt-8 border-t pt-6">
              <h3 className="text-lg font-semibold mb-4">
                Aperçu: {selectedTemplate.name}
              </h3>
              <div
                className="border border-gray-200 rounded p-4 bg-gray-50 max-h-96 overflow-y-auto"
                dangerouslySetInnerHTML={{ __html: selectedTemplate.content }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmailTemplateEditor;
